import { useParams, Link, Navigate } from "react-router-dom";
import { Calendar, Clock, ArrowLeft, ArrowRight, Tag } from "lucide-react";
import SEO from "@/components/site/SEO";
import QuoteCTA from "@/components/site/QuoteCTA";
import { Button } from "@/components/ui/button";
import { POSTS, POSTS_BY_SLUG } from "@/lib/blog";

function Block({ block }) {
  switch (block.type) {
    case "h2":
      return (
        <h2 className="font-display font-bold text-slate-900 text-2xl sm:text-3xl mt-12 mb-4">
          {block.text}
        </h2>
      );
    case "h3":
      return (
        <h3 className="font-display font-semibold text-slate-900 text-xl mt-8 mb-3">
          {block.text}
        </h3>
      );
    case "p":
      return <p className="text-slate-700 leading-relaxed mb-4">{block.text}</p>;
    case "ul":
      return (
        <ul className="list-disc list-outside pl-5 space-y-2 text-slate-700 mb-4">
          {block.items.map((it, i) => (
            <li key={i} className="leading-relaxed">{it}</li>
          ))}
        </ul>
      );
    case "ol":
      return (
        <ol className="list-decimal list-outside pl-5 space-y-2 text-slate-700 mb-4">
          {block.items.map((it, i) => (
            <li key={i} className="leading-relaxed">{it}</li>
          ))}
        </ol>
      );
    default:
      return null;
  }
}

export default function BlogPost() {
  const { slug } = useParams();
  const post = POSTS_BY_SLUG[slug];

  if (!post) return <Navigate to="/blog" replace />;

  const otherPosts = POSTS.filter((p) => p.slug !== slug).slice(0, 2);

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    mainEntityOfPage: { "@type": "WebPage", "@id": `https://901lots.com/blog/${post.slug}` },
    headline: post.title,
    description: post.description,
    image: post.image,
    datePublished: post.date,
    dateModified: post.date,
    author: { "@type": "Organization", name: "901 Lots", url: "https://901lots.com" },
    publisher: {
      "@type": "Organization",
      name: "901 Lots",
      logo: { "@type": "ImageObject", url: "https://901lots.com/logo.png" },
    },
    keywords: post.tags.join(", "),
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://901lots.com/" },
      { "@type": "ListItem", position: 2, name: "Blog", item: "https://901lots.com/blog" },
      { "@type": "ListItem", position: 3, name: post.title, item: `https://901lots.com/blog/${post.slug}` },
    ],
  };

  return (
    <div data-testid={`blog-post-${post.slug}`}>
      <SEO
        title={post.title}
        description={post.description}
        path={`/blog/${post.slug}`}
        image={post.image}
        type="article"
        jsonLd={[articleSchema, breadcrumbSchema]}
      />

      {/* Header */}
      <section className="relative bg-slate-50 border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
          <Link
            to="/blog"
            data-testid="blog-back-link"
            className="inline-flex items-center gap-1 text-sm font-medium text-blue-700 hover:text-blue-800"
          >
            <ArrowLeft className="h-4 w-4" /> All articles
          </Link>

          <div className="mt-6 flex flex-wrap items-center gap-2">
            {post.tags.map((t) => (
              <span
                key={t}
                className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-[0.18em] text-blue-700 bg-blue-50 border border-blue-100 rounded-full px-2.5 py-1"
              >
                <Tag className="h-3 w-3" />
                {t}
              </span>
            ))}
          </div>

          <h1 className="mt-4 font-display font-black tracking-tight text-slate-900 text-3xl sm:text-4xl lg:text-5xl leading-[1.1]">
            {post.title}
          </h1>

          <div className="mt-6 flex items-center gap-5 text-sm text-slate-600">
            <span className="inline-flex items-center gap-1.5">
              <Calendar className="h-4 w-4" />
              {new Date(post.date).toLocaleDateString(undefined, {
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Clock className="h-4 w-4" />
              {post.readTime}
            </span>
          </div>
        </div>
      </section>

      {/* Hero image */}
      <section className="bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-12">
          <div className="rounded-lg overflow-hidden border border-slate-200">
            <img src={post.image} alt={post.title} className="w-full h-72 sm:h-96 object-cover" />
          </div>
        </div>
      </section>

      {/* Body */}
      <article className="bg-white py-12 md:py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {post.blocks.map((b, i) => (
            <Block key={i} block={b} />
          ))}

          <div className="mt-12 p-6 bg-blue-50 border border-blue-200 rounded-lg flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h3 className="font-display font-semibold text-slate-900 text-lg">
                Need a free quote in the Mid-South?
              </h3>
              <p className="mt-1 text-sm text-slate-700">
                We'll walk your lot and send a fixed-price quote within 48 hours.
              </p>
            </div>
            <Button
              asChild
              className="bg-orange-600 hover:bg-orange-700 text-white font-semibold rounded-md"
              data-testid="blog-cta-quote"
            >
              <Link to="/quote">
                Request a Quote <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </article>

      {/* Other posts */}
      {otherPosts.length > 0 && (
        <section className="bg-slate-50 border-t border-slate-200 py-16">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-display font-bold text-slate-900 text-2xl">
              Keep reading
            </h2>
            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
              {otherPosts.map((p) => (
                <Link
                  key={p.slug}
                  to={`/blog/${p.slug}`}
                  className="group bg-white border border-slate-200 rounded-lg overflow-hidden hover:shadow-md transition-all"
                  data-testid={`related-${p.slug}`}
                >
                  <div className="aspect-[16/9] overflow-hidden">
                    <img src={p.image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <div className="p-5">
                    <h3 className="font-display font-semibold text-slate-900 text-lg group-hover:text-blue-700 transition-colors">
                      {p.title}
                    </h3>
                    <p className="mt-2 text-sm text-slate-600 line-clamp-2">
                      {p.description}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <QuoteCTA />
    </div>
  );
}
